/**
 * Sorties de stock — page de synthèse
 *
 * Tout ce qui quitte le stock SANS être vendu : pertes (DLC, casse, qualité,
 * écart), prélèvements internes, dons et sponsoring, échantillons.
 *
 * Le regroupement par catégorie n'est pas cosmétique : une casse est une charge
 * d'exploitation, un prélèvement de l'exploitant est une régularisation de TVA
 * (art. 33 CTVA, base = prix d'ACHAT), un don peut ouvrir une attestation. Les
 * additionner en un seul « total des sorties » n'aurait aucun sens comptable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { stockOutApi } from '../api/stock-out.api';
import { posApi } from '../api/pos.api';
import {
  StockOut,
  StockOutType,
  STOCK_OUT_TYPE_INFO,
  LossesReport,
  PersonalUseReport,
  ComptageFiche,
} from '../types/stock-out.types';
import { formatQty } from '../utils/pos.utils';

const PAGE_SIZE = 100;

/**
 * Filtre « Comptages sur fiche » : les comptages « Compter ce produit » à la baisse
 * ajustent le lot sans créer de sortie. Ils sont listés ici pour être retrouvés et
 * annulés sans rouvrir la fiche (22/10/2026 : un sachet retiré par erreur).
 */
const FILTRE_COMPTAGES = 'COMPTAGES' as const;
/** Sans filtre, seuls les plus récents s'affichent au-dessus des sorties. */
const APERCU_COMPTAGES = 5;

const formatCents = (c: number) => (c / 100).toFixed(2) + ' €';
const eur = (c: number) => (c / 100).toFixed(2).replace('.', ',');

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function presetRange(kind: string): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  if (kind === 'today') return { from: ymd(now), to: ymd(now) };
  if (kind === 'week') {
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    return { from: ymd(monday), to: ymd(now) };
  }
  if (kind === 'month') return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
  if (kind === 'lastmonth') return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
  if (kind === 'quarter') {
    const q = Math.floor(m / 3);
    return { from: ymd(new Date(y, q * 3, 1)), to: ymd(new Date(y, q * 3 + 3, 0)) };
  }
  if (kind === 'lastquarter') {
    const q = Math.floor(m / 3) - 1;
    const yy = q < 0 ? y - 1 : y;
    const qq = (q + 4) % 4;
    return { from: ymd(new Date(yy, qq * 3, 1)), to: ymd(new Date(yy, qq * 3 + 3, 0)) };
  }
  if (kind === 'year') return { from: ymd(new Date(y, 0, 1)), to: ymd(new Date(y, 11, 31)) };
  return { from: ymd(now), to: ymd(now) };
}

/**
 * Classes Tailwind écrites en toutes lettres : une classe construite à la volée
 * (`bg-${color}-100`) est supprimée au build par le purge et le badge sort blanc.
 */
const BADGE: Record<string, string> = {
  red: 'bg-red-100 text-red-800 border-red-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200',
  gray: 'bg-gray-100 text-gray-700 border-gray-200',
  green: 'bg-green-100 text-green-800 border-green-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  cyan: 'bg-cyan-100 text-cyan-800 border-cyan-200',
};

const CATEGORY_LABEL: Record<string, string> = {
  loss: 'Pertes (démarque)',
  internal: 'Prélèvements internes',
  external: 'Dons, sponsoring, retours',
  other: 'Échantillons & invendus',
};

const CATEGORY_HINT: Record<string, string> = {
  loss: 'Charge d’exploitation. C’est la marchandise achetée puis perdue.',
  internal: 'Régularisation TVA sur le prix d’achat (art. 33 CTVA) — onglet TVA.',
  external: 'Sorties valorisées vers un tiers : attestation ou charge publicitaire.',
  other: 'Dégustations, échantillons clients, invendus de fin de journée.',
};

const CATEGORY_ORDER: Array<'loss' | 'internal' | 'external' | 'other'> = [
  'loss',
  'internal',
  'external',
  'other',
];

function typeInfo(type: StockOutType) {
  return (
    STOCK_OUT_TYPE_INFO[type] || {
      type,
      label: String(type),
      icon: '•',
      color: 'gray',
      category: 'other' as const,
      requiresBeneficiary: false,
      requiresSupplier: false,
      vatApplicable: false,
    }
  );
}

function TypeBadge({ type }: { type: StockOutType }) {
  const info = typeInfo(type);
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${
        BADGE[info.color] || BADGE.gray
      }`}
    >
      <span aria-hidden="true">{info.icon}</span>
      {info.label}
    </span>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
    </div>
  );
}

function downloadCsv(filename: string, lines: string[]) {
  // BOM : sans lui Excel lit le CSV en latin-1 et casse les accents.
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const csvCell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;

export default function StockOuts() {
  const initial = presetRange('month');
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [tab, setTab] = useState<'summary' | 'detail' | 'vat'>('summary');

  const [report, setReport] = useState<LossesReport | null>(null);
  const [vatReport, setVatReport] = useState<PersonalUseReport | null>(null);
  const [items, setItems] = useState<StockOut[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [typeFilter, setTypeFilter] = useState<StockOutType | '' | typeof FILTRE_COMPTAGES>('');
  const [comptages, setComptages] = useState<ComptageFiche[]>([]);
  const [comptagesTronques, setComptagesTronques] = useState(false);
  const [tousLesComptages, setTousLesComptages] = useState(false);
  const [annulationComptage, setAnnulationComptage] = useState<string | null>(null);
  const dernierChargementListe = useRef(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Les bornes sont des jours pleins : sans l'heure de fin, une sortie encodée
  // à 14 h le dernier jour tomberait hors période.
  const range = useMemo(
    () => ({ start: `${from}T00:00:00.000Z`, end: `${to}T23:59:59.999Z` }),
    [from, to],
  );

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [losses, vat] = await Promise.all([
        stockOutApi.getLossesReport(range.start, range.end),
        stockOutApi.getPersonalUseReport(range.start, range.end),
      ]);
      setReport(losses);
      setVatReport(vat);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [range]);

  const loadList = useCallback(
    async (nextOffset: number) => {
      // Seule la DERNIÈRE demande écrit à l'écran : changer de filtre pendant un
      // chargement laissait la réponse précédente arriver après coup et réafficher
      // la mauvaise liste (vu au banc, 22/10/2026).
      const numero = ++dernierChargementListe.current;
      // Filtre « Comptages sur fiche » : aucune sortie à charger.
      if (typeFilter === FILTRE_COMPTAGES) {
        setItems([]);
        setTotal(0);
        setOffset(0);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await stockOutApi.list({
          startDate: range.start,
          endDate: range.end,
          type: typeFilter || undefined,
          limit: PAGE_SIZE,
          offset: nextOffset,
        });
        if (numero !== dernierChargementListe.current) return;
        setItems(res.items);
        setTotal(res.total);
        setOffset(res.offset);
      } catch (e) {
        if (numero !== dernierChargementListe.current) return;
        setError(e instanceof Error ? e.message : 'Erreur de chargement');
      } finally {
        if (numero === dernierChargementListe.current) setLoading(false);
      }
    },
    [range, typeFilter],
  );

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (tab === 'detail') void loadList(0);
  }, [tab, loadList]);

  const loadComptages = useCallback(async () => {
    try {
      const res = await stockOutApi.listComptages(range.start, range.end);
      setComptages(res.items);
      setComptagesTronques(res.tronque);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement des comptages');
    }
  }, [range]);

  useEffect(() => {
    if (tab === 'detail') void loadComptages();
  }, [tab, loadComptages]);

  const handleAnnulerComptage = async (c: ComptageFiche) => {
    const quantite = `${formatQty(c.quantite)} ${c.unite || ''}`.trim();
    const ou = c.emplacement === 'reserve' ? 'en réserve' : 'en rayon';
    const ok = window.confirm(
      `Annuler ce comptage ?\n\n` +
        `${c.product_name} : −${quantite} (${c.motif})\n` +
        `${new Date(c.date).toLocaleString('fr-BE')}${c.lot_number ? ` — lot ${c.lot_number}` : ''}\n\n` +
        `Le lot retrouve ${quantite} ${ou}. Le comptage reste dans l'historique, marqué ` +
        `« annulé », et sort de la démarque.`,
    );
    if (!ok) return;

    setAnnulationComptage(c.id);
    setNotice(null);
    setError(null);
    try {
      await posApi.annulerComptage(c.id);
      setNotice(
        `Comptage annulé : ${c.product_name} — ${c.lot_number ? `le lot ${c.lot_number}` : 'le lot'} retrouve ${quantite} ${ou}.`,
      );
      await Promise.all([loadComptages(), loadSummary()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Échec de l'annulation du comptage");
    } finally {
      setAnnulationComptage(null);
    }
  };

  const comptagesVisibles =
    typeFilter === FILTRE_COMPTAGES || tousLesComptages ? comptages : comptages.slice(0, APERCU_COMPTAGES);
  const comptagesEnDemarqueCents = comptages.reduce((s, c) => s + (c.en_demarque ? c.cout_cents : 0), 0);

  const handleDelete = async (so: StockOut) => {
    const info = typeInfo(so.type);
    const ok = window.confirm(
      `Annuler cette sortie ?\n\n` +
        `${so.reference} — ${info.label}\n` +
        `${so.product_name} : ${formatQty(so.quantity)} ${so.unit || ''}\n\n` +
        `Le stock sera rendu au lot d'origine. La ligne sort du rapport de pertes mais RESTE ` +
        `au registre, marquée « annulée » — un registre de démarque ne s'efface pas.`,
    );
    if (!ok) return;

    setDeleting(so.id);
    setNotice(null);
    try {
      const res = await stockOutApi.delete(so.id);
      setNotice(
        res.approximate
          ? `Sortie ${so.reference} annulée. ⚠️ Sortie ancienne : le stock a été rendu au RAYON faute de savoir d'où il venait — vérifie le lot ${so.lot_number || ''}.`
          : `Sortie ${so.reference} annulée, stock rendu au lot d'origine.`,
      );
      await Promise.all([loadList(offset), loadSummary()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Échec de l'annulation");
    } finally {
      setDeleting(null);
    }
  };

  // Regroupement par catégorie comptable, calculé sur by_type du rapport.
  const byCategory = useMemo(() => {
    const acc: Record<string, { count: number; cost: number; sale: number }> = {};
    for (const row of report?.by_type || []) {
      const cat = typeInfo(row.type).category;
      const cur = acc[cat] || { count: 0, cost: 0, sale: 0 };
      acc[cat] = {
        count: cur.count + row.count,
        cost: cur.cost + row.total_cost_cents,
        sale: cur.sale + row.total_sale_cents,
      };
    }
    return acc;
  }, [report]);

  const sortedTypes = useMemo(
    () => [...(report?.by_type || [])].sort((a, b) => b.total_cost_cents - a.total_cost_cents),
    [report],
  );

  const exportSummaryCsv = () => {
    if (!report) return;
    const L: string[] = [];
    L.push(`Sorties de stock;${from} au ${to}`);
    L.push('');
    L.push('PAR CATEGORIE;Nombre;Cout achat;Valeur vente');
    for (const cat of CATEGORY_ORDER) {
      const d = byCategory[cat];
      if (!d) continue;
      L.push(`${csvCell(CATEGORY_LABEL[cat])};${d.count};${eur(d.cost)};${eur(d.sale)}`);
    }
    L.push('');
    L.push('PAR TYPE;Nombre;Cout achat;Valeur vente');
    for (const row of sortedTypes) {
      L.push(
        `${csvCell(typeInfo(row.type).label)};${row.count};${eur(row.total_cost_cents)};${eur(row.total_sale_cents)}`,
      );
    }
    L.push('');
    L.push('TOP PRODUITS;Quantite;Cout achat');
    for (const p of report.top_products) {
      L.push(`${csvCell(p.product_name)};${formatQty(p.total_quantity)} ${p.unit || ''};${eur(p.total_cost_cents)}`);
    }
    if (report.adjustments && report.adjustments.count > 0) {
      L.push('');
      L.push('ENCODE DEPUIS LA FICHE PRODUIT;Nombre;Cout achat;Compte en demarque');
      for (const row of report.adjustments.by_reason) {
        L.push(
          `${csvCell(row.reason)};${row.count};${eur(row.total_cost_cents)};${row.is_loss ? 'oui' : 'non'}`,
        );
      }
    }
    L.push('');
    L.push(`TOTAL SORTIES;${report.total_count};${eur(report.total_cost_cents)};${eur(report.total_sale_cents)}`);
    if (report.adjustments && report.adjustments.count > 0) {
      L.push(`TOTAL AVEC LES FICHES;;${eur(report.combined_cost_cents ?? report.total_cost_cents)};`);
    }
    downloadCsv(`sorties-stock-${from}_${to}.csv`, L);
  };

  const exportVatCsv = () => {
    if (!vatReport) return;
    const L: string[] = [];
    L.push(`Prelevements (base TVA art. 33 CTVA);${from} au ${to}`);
    L.push('');
    L.push('Date;Produit;Quantite;Beneficiaire;Valeur vente;Taux TVA;TVA due');
    for (const it of vatReport.items) {
      L.push(
        [
          csvCell(new Date(it.recorded_at).toLocaleDateString('fr-BE')),
          csvCell(it.product_name),
          csvCell(`${formatQty(it.quantity)} ${it.unit || ''}`),
          csvCell(it.beneficiary_name),
          eur(it.sale_price_cents),
          `${it.vat_rate}%`,
          eur(it.vat_amount_cents),
        ].join(';'),
      );
    }
    L.push('');
    L.push('TAUX;Base (prix achat HT);TVA due');
    for (const [rate, d] of Object.entries(vatReport.totals.by_vat_rate)) {
      L.push(`${rate}%;${eur(d.base_cents)};${eur(d.vat_cents)}`);
    }
    L.push(`TOTAL;;${eur(vatReport.totals.total_vat_cents)}`);
    downloadCsv(`prelevements-tva-${from}_${to}.csv`, L);
  };

  const presets: Array<{ key: string; label: string }> = [
    { key: 'today', label: "Aujourd'hui" },
    { key: 'week', label: 'Cette semaine' },
    { key: 'month', label: 'Ce mois' },
    { key: 'lastmonth', label: 'Mois dernier' },
    { key: 'quarter', label: 'Ce trimestre' },
    { key: 'lastquarter', label: 'Trimestre dernier' },
    { key: 'year', label: 'Cette année' },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <header className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Sorties de stock</h1>
        <p className="text-sm text-gray-600 mt-1">
          Tout ce qui quitte le stock sans être vendu : pertes, prélèvements, dons, échantillons.
        </p>
      </header>

      {/* Période */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-5">
        <div className="flex flex-wrap gap-2 mb-3">
          {presets.map((p) => (
            <button
              key={p.key}
              onClick={() => {
                const r = presetRange(p.key);
                setFrom(r.from);
                setTo(r.to);
              }}
              className="px-3 py-1.5 text-sm rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 min-h-[36px]"
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-700">
            <span className="block text-xs text-gray-500 mb-1">Du</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 min-h-[40px]"
            />
          </label>
          <label className="text-sm text-gray-700">
            <span className="block text-xs text-gray-500 mb-1">Au</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 min-h-[40px]"
            />
          </label>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 border-b border-gray-200 mb-5">
        {([
          { key: 'summary', label: 'Synthèse' },
          { key: 'detail', label: 'Détail' },
          { key: 'vat', label: 'TVA — prélèvements' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px min-h-[40px] ${
              tab === t.key
                ? 'border-[#2d6a4f] text-[#2d6a4f]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-sm flex justify-between gap-3">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-blue-700 font-medium">
            ✕
          </button>
        </div>
      )}
      {loading && <div className="mb-4 text-sm text-gray-500">Chargement…</div>}

      {/* ---------- SYNTHÈSE ---------- */}
      {tab === 'summary' && report && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Kpi label="Sorties" value={String(report.total_count)} hint="Lignes enregistrées ici" />
            <Kpi
              label="Coût d'achat"
              value={formatCents(report.total_cost_cents)}
              hint="Sorties de cet écran"
            />
            <Kpi
              label="Total avec les fiches"
              value={formatCents(report.combined_cost_cents ?? report.total_cost_cents)}
              hint="Les deux canaux réunis"
            />
            <Kpi
              label="Valeur de vente"
              value={formatCents(report.total_sale_cents)}
              hint="Ce que les sorties auraient rapporté"
            />
          </div>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Par nature</h2>
              <button
                onClick={exportSummaryCsv}
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 min-h-[40px]"
              >
                Exporter en CSV
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CATEGORY_ORDER.filter((c) => byCategory[c]).map((cat) => {
                const d = byCategory[cat];
                return (
                  <div key={cat} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="font-semibold text-gray-900">{CATEGORY_LABEL[cat]}</div>
                    <div className="text-xs text-gray-500 mt-0.5 mb-3">{CATEGORY_HINT[cat]}</div>
                    <div className="flex justify-between text-sm text-gray-700">
                      <span>{d.count} sortie{d.count > 1 ? 's' : ''}</span>
                      <span className="font-semibold tabular-nums">{formatCents(d.cost)}</span>
                    </div>
                  </div>
                );
              })}
              {CATEGORY_ORDER.every((c) => !byCategory[c]) && (
                <div className="text-sm text-gray-500">Aucune sortie sur cette période.</div>
              )}
            </div>
          </section>

          {sortedTypes.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Par type</h2>
              <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Type</th>
                      <th className="text-right px-4 py-2 font-medium">Nombre</th>
                      <th className="text-right px-4 py-2 font-medium">Coût d'achat</th>
                      <th className="text-right px-4 py-2 font-medium">Valeur de vente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTypes.map((row) => (
                      <tr key={row.type} className="border-t border-gray-100">
                        <td className="px-4 py-2">
                          <TypeBadge type={row.type} />
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{row.count}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">
                          {formatCents(row.total_cost_cents)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                          {formatCents(row.total_sale_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {report.adjustments && report.adjustments.count > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Encodé depuis la fiche produit
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Les comptages faits sur une fiche (« Compter ce produit ») ajustent le lot au lieu
                de créer une sortie. Ils comptent pourtant dans la démarque — sauf les corrections
                de saisie, où la marchandise n'a jamais été là. Ils sont listés un par un dans
                l'onglet Détail, où un comptage fait par erreur s'annule pendant 7 jours.
              </p>
              <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Motif</th>
                      <th className="text-right px-4 py-2 font-medium">Nombre</th>
                      <th className="text-right px-4 py-2 font-medium">Coût d'achat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.adjustments.by_reason.map((row) => (
                      <tr key={row.reason} className="border-t border-gray-100">
                        <td className="px-4 py-2">
                          {row.reason}
                          {!row.is_loss && (
                            <span className="ml-2 text-xs text-gray-500">
                              correction, hors démarque
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{row.count}</td>
                        <td
                          className={
                            'px-4 py-2 text-right tabular-nums ' +
                            (row.is_loss ? 'font-medium' : 'text-gray-400')
                          }
                        >
                          {formatCents(row.total_cost_cents)}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-gray-300 bg-gray-50">
                      <td className="px-4 py-2 font-semibold">Compté dans la démarque</td>
                      <td className="px-4 py-2"></td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">
                        {formatCents(report.adjustments.loss_cost_cents)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {report.top_products.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Produits les plus coûteux</h2>
              <p className="text-xs text-gray-500 mb-3">
                Classés par coût d'achat perdu — les 10 premiers de la période.
              </p>
              <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Produit</th>
                      <th className="text-right px-4 py-2 font-medium">Quantité</th>
                      <th className="text-right px-4 py-2 font-medium">Coût d'achat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.top_products.map((p) => (
                      <tr key={p.product_id} className="border-t border-gray-100">
                        <td className="px-4 py-2">{p.product_name}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatQty(p.total_quantity)} {p.unit || ''}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">
                          {formatCents(p.total_cost_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      {/* ---------- DÉTAIL ---------- */}
      {tab === 'detail' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-gray-700">
              <span className="block text-xs text-gray-500 mb-1">Type</span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as StockOutType | '' | typeof FILTRE_COMPTAGES)}
                className="border border-gray-300 rounded-lg px-3 py-2 min-h-[40px]"
              >
                <option value="">Tous les types</option>
                <option value={FILTRE_COMPTAGES}>Comptages sur fiche (« Compter ce produit »)</option>
                {Object.values(StockOutType).map((t) => (
                  <option key={t} value={t}>
                    {typeInfo(t).label}
                  </option>
                ))}
              </select>
            </label>
            <div className="text-sm text-gray-600 self-end pb-2">
              {typeFilter === FILTRE_COMPTAGES ? (
                <>
                  {comptages.length}
                  {comptagesTronques ? '+' : ''} comptage{comptages.length > 1 ? 's' : ''} sur la période
                </>
              ) : (
                <>
                  {total} sortie{total > 1 ? 's' : ''} sur la période
                  {total > PAGE_SIZE && (
                    <span className="text-gray-500">
                      {' '}
                      — affichage {offset + 1}–{Math.min(offset + PAGE_SIZE, total)}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Comptages « Compter ce produit » à la baisse : ils ajustent le lot sans
              créer de sortie, d'où ce bloc à part — avec leur propre annulation. */}
          {(typeFilter === '' || typeFilter === FILTRE_COMPTAGES) && comptages.length > 0 && (
            <section className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-base font-semibold text-gray-900">
                  Comptages faits sur une fiche (« Compter ce produit »)
                </h2>
                <span className="text-sm text-gray-600 tabular-nums">
                  {comptages.length}
                  {comptagesTronques ? '+' : ''} sur la période · {formatCents(comptagesEnDemarqueCents)} en démarque
                </span>
              </div>
              <p className="text-xs text-gray-600">
                Ils ajustent le lot sans créer de sortie. Un comptage fait par erreur s'annule ici
                pendant 7 jours : le lot retrouve sa quantité et la ligne sort de la démarque.
              </p>
              <div className="space-y-2">
                {comptagesVisibles.map((c) => (
                  <div
                    key={c.id}
                    className={
                      'rounded-lg border p-3 flex flex-wrap sm:flex-nowrap gap-3 items-start ' +
                      (c.annule ? 'bg-gray-50 border-gray-300 opacity-70' : 'bg-white border-gray-200')
                    }
                  >
                    {c.photo_url ? (
                      <a href={c.photo_url} target="_blank" rel="noreferrer" className="flex-none">
                        <img
                          src={c.photo_url}
                          alt="Photo du comptage"
                          className="w-14 h-14 rounded object-cover border border-gray-200"
                        />
                      </a>
                    ) : (
                      <div className="w-14 h-14 rounded bg-gray-50 border border-gray-200 flex-none" />
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap bg-indigo-100 text-indigo-800 border-indigo-200">
                          Comptage — {c.motif}
                        </span>
                        <span className="font-medium text-gray-900 truncate">{c.product_name}</span>
                        {!c.annule && !c.en_demarque && (
                          <span className="text-xs text-gray-500">correction, hors démarque</span>
                        )}
                        {c.annule && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                            annulé — stock rendu au lot
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-700 mt-1 tabular-nums">
                        −{formatQty(c.quantite)} {c.unite || ''} {c.emplacement === 'reserve' ? 'en réserve' : 'en rayon'} ·{' '}
                        {formatCents(c.cout_cents)} d'achat
                      </div>
                      {c.remarque && <div className="text-sm text-gray-600 mt-1 italic">« {c.remarque} »</div>}
                      <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
                        <span>{new Date(c.date).toLocaleString('fr-BE')}</span>
                        {c.lot_number && <span>Lot {c.lot_number}</span>}
                        {c.par && <span>par {c.par}</span>}
                      </div>
                    </div>

                    {c.annulable ? (
                      <button
                        onClick={() => handleAnnulerComptage(c)}
                        disabled={annulationComptage === c.id}
                        className="flex-none px-3 py-2 text-sm rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 min-h-[40px]"
                      >
                        {annulationComptage === c.id ? '…' : 'Annuler'}
                      </button>
                    ) : (
                      !c.annule && (
                        <span className="flex-none text-xs text-gray-500 max-w-[10rem]">
                          Trop ancien pour être annulé : recompter le produit.
                        </span>
                      )
                    )}
                  </div>
                ))}
              </div>
              {typeFilter === '' && comptages.length > APERCU_COMPTAGES && (
                <button
                  onClick={() => setTousLesComptages((v) => !v)}
                  className="text-sm font-medium text-indigo-800 hover:underline min-h-[40px]"
                >
                  {tousLesComptages
                    ? `Ne montrer que les ${APERCU_COMPTAGES} plus récents`
                    : `Voir les ${comptages.length - APERCU_COMPTAGES} autres comptages`}
                </button>
              )}
            </section>
          )}

          {typeFilter === FILTRE_COMPTAGES && comptages.length === 0 && !loading && (
            <div className="text-sm text-gray-500">Aucun comptage à la baisse sur cette période.</div>
          )}

          {typeFilter !== FILTRE_COMPTAGES && items.length === 0 && !loading && (
            <div className="text-sm text-gray-500">Aucune sortie sur cette période.</div>
          )}

          <div className="space-y-2">
            {(typeFilter === FILTRE_COMPTAGES ? [] : items).map((so) => (
              <div
                key={so.id}
                className={
                  'rounded-lg border p-3 flex flex-wrap sm:flex-nowrap gap-3 items-start ' +
                  (so.status === 'cancelled'
                    ? 'bg-gray-50 border-gray-300 opacity-70'
                    : 'bg-white border-gray-200')
                }
              >
                {so.photo_url ? (
                  <a href={so.photo_url} target="_blank" rel="noreferrer" className="flex-none">
                    <img
                      src={so.photo_url}
                      alt="Photo de la sortie"
                      className="w-14 h-14 rounded object-cover border border-gray-200"
                    />
                  </a>
                ) : (
                  <div className="w-14 h-14 rounded bg-gray-50 border border-gray-200 flex-none" />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <TypeBadge type={so.type} />
                    <span className="font-medium text-gray-900 truncate">{so.product_name}</span>
                    {so.status === 'cancelled' && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                        annulée — conservée au registre
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700 mt-1 tabular-nums">
                    {formatQty(so.quantity)} {so.unit || ''} · {formatCents(so.cost_price_cents)} d'achat
                    <span className="text-gray-500"> · vente {formatCents(so.sale_price_cents)}</span>
                  </div>
                  {so.reason && <div className="text-sm text-gray-600 mt-1 italic">« {so.reason} »</div>}
                  <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
                    <span>{new Date(so.recorded_at).toLocaleString('fr-BE')}</span>
                    <span className="font-mono">{so.reference}</span>
                    {so.lot_number && <span>Lot {so.lot_number}</span>}
                    {so.beneficiary_name && <span>Pour {so.beneficiary_name}</span>}
                    {so.recorded_by_name && <span>par {so.recorded_by_name}</span>}
                  </div>
                </div>

                {so.status !== 'cancelled' && (
                  <button
                    onClick={() => handleDelete(so)}
                    disabled={deleting === so.id}
                    className="flex-none px-3 py-2 text-sm rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 min-h-[40px]"
                  >
                    {deleting === so.id ? '…' : 'Annuler'}
                  </button>
                )}
              </div>
            ))}
          </div>

          {total > PAGE_SIZE && (
            <div className="flex justify-between items-center pt-2">
              <button
                onClick={() => loadList(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0 || loading}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 disabled:opacity-40 min-h-[40px]"
              >
                ← Précédent
              </button>
              <button
                onClick={() => loadList(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total || loading}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 disabled:opacity-40 min-h-[40px]"
              >
                Suivant →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---------- TVA ---------- */}
      {tab === 'vat' && vatReport && (
        <div className="space-y-5">
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            Prélèvements de l'exploitant et repas du personnel. La TVA due se calcule sur le{' '}
            <strong>prix d'achat</strong> (art. 33 du Code de la TVA), pas sur le prix de vente —
            c'est ce que reprend la colonne « TVA due ». À transmettre à Clearfacts avec la
            déclaration trimestrielle.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Kpi
              label="Valeur de vente"
              value={formatCents(vatReport.totals.total_sale_cents)}
              hint={`${vatReport.items.length} prélèvement${vatReport.items.length > 1 ? 's' : ''}`}
            />
            <Kpi label="TVA due" value={formatCents(vatReport.totals.total_vat_cents)} />
          </div>

          {Object.keys(vatReport.totals.by_vat_rate).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Taux</th>
                    <th className="text-right px-4 py-2 font-medium">Base (prix d'achat HT)</th>
                    <th className="text-right px-4 py-2 font-medium">TVA due</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(vatReport.totals.by_vat_rate).map(([rate, d]) => (
                    <tr key={rate} className="border-t border-gray-100">
                      <td className="px-4 py-2">{rate} %</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatCents(d.base_cents)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">
                        {formatCents(d.vat_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Détail des prélèvements</h2>
            <button
              onClick={exportVatCsv}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 min-h-[40px]"
            >
              Exporter en CSV
            </button>
          </div>

          {vatReport.items.length === 0 ? (
            <div className="text-sm text-gray-500">Aucun prélèvement sur cette période.</div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Date</th>
                    <th className="text-left px-4 py-2 font-medium">Produit</th>
                    <th className="text-right px-4 py-2 font-medium">Quantité</th>
                    <th className="text-left px-4 py-2 font-medium">Bénéficiaire</th>
                    <th className="text-right px-4 py-2 font-medium">Valeur vente</th>
                    <th className="text-right px-4 py-2 font-medium">TVA due</th>
                  </tr>
                </thead>
                <tbody>
                  {vatReport.items.map((it) => (
                    <tr key={it.id} className="border-t border-gray-100">
                      <td className="px-4 py-2 whitespace-nowrap">
                        {new Date(it.recorded_at).toLocaleDateString('fr-BE')}
                      </td>
                      <td className="px-4 py-2">{it.product_name}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatQty(it.quantity)} {it.unit || ''}
                      </td>
                      <td className="px-4 py-2">{it.beneficiary_name}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatCents(it.sale_price_cents)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">
                        {formatCents(it.vat_amount_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
